import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Label,
  Option,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useAtom, useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { personaAtom, currentPreferencesAtom } from "@/store";
import { getRoutesForRole } from "@/config/routes";
import type { ThemePreference } from "@/store";

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
});

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

interface PreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

export function PreferencesDialog({ open, onClose }: PreferencesDialogProps) {
  const { t } = useTranslation();
  const styles = useStyles();
  const persona = useAtomValue(personaAtom);
  const [preferences, setPreferences] = useAtom(currentPreferencesAtom);
  const availableRoutes = getRoutesForRole(persona.role);

  return (
    <Dialog
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Preferences</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field
                label="Default landing page"
                hint="Page to show after login or persona switch."
              >
                <Dropdown
                  value={
                    availableRoutes.find(
                      (r) => r.path === preferences.defaultRoute,
                    )
                      ? t(
                          availableRoutes.find(
                            (r) => r.path === preferences.defaultRoute,
                          )!.labelKey,
                        )
                      : t(availableRoutes[0]?.labelKey ?? "nav.home")
                  }
                  selectedOptions={[preferences.defaultRoute]}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      setPreferences({ defaultRoute: data.optionValue });
                    }
                  }}
                >
                  {availableRoutes.map((route) => (
                    <Option key={route.key} value={route.path}>
                      {t(route.labelKey)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <Field
                label="Theme"
                hint="System follows your operating system preference."
              >
                <Dropdown
                  value={
                    THEME_OPTIONS.find((o) => o.value === preferences.theme)
                      ?.label ?? "System"
                  }
                  selectedOptions={[preferences.theme]}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      setPreferences({
                        theme: data.optionValue as ThemePreference,
                      });
                    }
                  }}
                >
                  {THEME_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <div className={styles.row}>
                <Label>Navigation</Label>
                <Switch
                  label="Collapse navigation rail by default"
                  checked={preferences.navCollapsed}
                  onChange={(_event, data) => {
                    setPreferences({ navCollapsed: data.checked });
                  }}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
